import axios from 'axios'

import { router } from './main.js'

import Raven from 'raven-js'

import * as action from './actionTypes'

import * as mutation from './mutationTypes'

const is404Error = error =>
  error.response != null && error.response.status === 404

const isBadRequest = error =>
  error.response != null && error.response.status === 400

const actions = {
  [action.isAuthenticated] ({ commit }) {
    return axios.get('/api/v1/rest-auth/user/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(res => res.data)
      .then(user => {
        const token = localStorage.getItem('token')
        commit('authenticateUser', { user, token })
      })
      .catch(error => {
        console.warn('Problem authenticating user.', error)
        throw error
      })
  },
  [action.passwordReset] ({ commit }, { email }) {
    return axios.post('/api/v1/rest-auth/password/reset/', { email })
      .then(() => {
        commit(mutation.notify, {
          message: `Sent password reset email to ${email}`,
          level: 'success',
        })
      })
      .catch(error => {
        if (isBadRequest(error)) {
          commit(mutation.notify, {
            message: `Not a vaild email: ${email}`,
            level: 'danger',
            sticky: true,
          })
        } else {
          commit(mutation.notify, {
            message: `Error sending password reset email to ${email}`,
            level: 'danger',
            sticky: true,
          })
          Raven.captureException(error)
        }
        throw error
      })
  },
  [action.resendEmailConfirmation] ({ commit }) {
    commit('loadingResendEmailConfirmation', true)
    return axios.post('/api/v1/resend-email-confirmation/', {},
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        router.push('/')
        commit('loadingResendEmailConfirmation', false)
        commit(mutation.notify, { message: 'Email confirmation sent to ' + response.data.email })
      })
      .catch(error => {
        commit('loadingResendEmailConfirmation', false)
        commit(mutation.notify, { message: 'Problem sending email', level: 'danger' })
        throw error
      })
  },
  confirmEmail ({ dispatch, commit }, { key }) {
    return axios.post('/api/v1/rest-auth/registration/verify-email/', { key })
      .then(response => response.data)
      .then(() => {
        dispatch(action.isAuthenticated)
        router.push('/')
        commit(mutation.notify, { message: 'Your email is now confirmed', level: 'success' })
      })
      .catch(error => {
        commit(mutation.notify, { message: "Oops, we couldn't confirm your email address", level: 'danger' })
        router.push('/')
        Raven.captureException(error)
      })
  },

  [action.logout] ({ commit }) {
    return axios.post('/api/v1/rest-auth/logout/', {},
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit(mutation.logout)
      router.push('/login')
    })
    .catch(error => {
      Raven.captureException(error)
    })
  },
  [action.login] ({ commit }, { email, password }) {
    return axios.post('/api/v1/rest-auth/login/', { email, password })
      .then(response => response.data.key)
      .then(token => {
        commit(mutation.setLoginErrorMessage, '')
        commit('authenticateUser', { token })
        router.push('/')
      })
      .catch(error => {
        // https://github.com/mzabriskie/axios#handling-errors
        // TODO: move this into a function and generalize to use elsewhere
        if (error.response) {
          if (error.response.data.non_field_errors[0] === 'User account is disabled.') {
            commit(mutation.setLoginErrorMessage, 'This account has been deactivated. Contact support@classforward.com if you believe this is an error.')
          } else {
            commit(mutation.setLoginErrorMessage, 'incorrect email or password')
          }
        } else if (error.request) {
          commit(mutation.setLoginErrorMessage, 'problem contacting server')
        } else {
          commit(mutation.setLoginErrorMessage, 'problem logging in')
        }
        Raven.captureException(error)
      })
  },
  magicLogin ({ commit, dispatch }, { magicToken }) {
    return axios.post('/api/v1/magic-login/', { magicToken })
      .then(response => response.data.token)
      .then(token => {
        commit(mutation.setLoginErrorMessage, '')
        commit('authenticateUser', { token })
        router.push('/')
      })
      .catch(error => {
        dispatch(action.generalErrorHandler, { error })
        router.push('/')
        throw error
      })
  },

  updatePassword (_, { newPassword1, newPassword2, oldPassword }) {
    return axios.post('/api/v1/rest-auth/password/change/', {
      new_password1: newPassword1,
      new_password2: newPassword2,
      old_password: oldPassword,
    },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
  },

  /**
   * Update user via patch request
   *
   * @param {Object} val - Key values of fields to update on user model
   * @returns {Promise} Completes after success for failure
   */
  [action.updateUser] ({ commit, dispatch }, val) {
    return axios.patch('/api/v1/rest-auth/user/', val,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit(mutation.updateUser, response.data)
    })
    .catch(error => {
      dispatch(action.generalErrorHandler, {
        error,
        message: "Oops, we couldn't complete your request."
      })
      throw error
    })
  },

  [action.generalErrorHandler] ({ commit }, { error, customMessage }) {
    // For null messages, we set a default, but for 'false', we don't set a notification
    if (customMessage !== false) {
      const message = customMessage || "Oops, that didn't work"
      commit(mutation.notify, { message, level: 'danger' })
    }
    if (typeof error === 'string') {
      return Raven.captureMessage(error)
    } else {
      return Raven.captureException(error)
    }
  },

  fetchFolders ({ commit }) {
    commit('setLoadingFolders', true)
    return axios.get('/api/v1/folders/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        commit('updateFolders', response.data)
        commit('setLoadingFolders', false)
        return response
      })
      .catch(error => {
        console.warn('error fetching folders', error)
        commit('setLoadingFolders', false)
        throw error
      })
  },

  updateVideoFolder ({ commit }, { videoID, folder }) {
    return axios.patch(`/api/v1/videos/${videoID}/`, { folder },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('updateVideoFolder', { videoID, folder })
    })
    .catch(error => {
      console.warn('error updating video folder', error)
      throw error
    })
  },
  createFolder ({ commit }, { title }) {
    return axios.post('/api/v1/folders/', { title, description: 'blank' },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('addFolder', response.data)
    })
    .catch(error => {
      console.warn('error creating folder', error)
      throw error
    })
  },

  fetchResources ({ commit }) {
    commit('setLoadingResources', true)
    return axios.get('/api/v1/resources/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('updateResources', response.data)
      commit('setLoadingResources', false)
    })
    .catch(error => {
      console.warn('error fetching resources', error)
      commit('setLoadingResources', false)
      throw error
    })
  },
  [action.fetchVideoBySlug] ({ commit }, slug) {
    commit('setLoadingVideos', true)
    return axios.get(`/api/v1/v/${slug}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit(mutation.setVideo, response.data)
      commit('setLoadingVideos', false)
      return response.data.id
    })
    .catch(error => {
      console.warn('error fetching video', error)
      commit('setLoadingVideos', false)
      throw error
    })
  },
  [action.fetchVideoByID] ({ commit }, id) {
    return axios.get(`/api/v1/videos/${id}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit(mutation.setVideo, response.data)
    })
    .catch(error => {
      if (!is404Error(error)) {
        Raven.captureException(error)
      }
      throw error
    })
  },
  fetchVideos ({ commit }) {
    commit('setLoadingVideos', true)
    return axios.get('/api/v1/videos/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('updateVideos', response.data)
      commit('setLoadingVideos', false)
    })
    .catch(error => {
      console.warn('error fetching videos', error)
      commit('setLoadingVideos', false)
      throw error
    })
  },
  fetchVideosSharedWithMe ({ commit }) {
    commit('setLoadingVideos', true)
    return axios.get('/api/v1/videos/shared/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('updateVideosSharedWithMe', response.data)
      commit('setLoadingVideos', false)
    })
    .catch(error => {
      console.warn('error fetching videos', error)
      commit('setLoadingVideos', false)
      throw error
    })
  },
  createVideo ({ commit }, data) {
    commit('setSendingToProcessing', true)
    return axios.post('/api/v1/videos/', data,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('setSendingToProcessing', false)
      commit('setShowAddVideoModal', false)
      commit(mutation.addVideo, response.data)
      router.push('/videos/' + response.data.id)
    })
    .catch(error => {
      commit('setSendingToProcessing', false)
      console.warn('error with creating video', error)
      throw error
    })
  },
  updateVideo ({ commit }, { videoID, title, description }) {
    return axios.patch(`/api/v1/videos/${videoID}/`, { title, description },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      const newTitle = response.data.title
      const newDescription = response.data.description
      commit('updateVideo', { videoID, newTitle, newDescription })
    })
    .catch(error => {
      console.warn('error updating video info', error)
      throw error
    })
  },
  updateVideoSlugSharing ({ commit }, { videoID, val }) {
    return axios.patch(`/api/v1/videos/${videoID}/`, { slug_share_enabled: val },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('updateVideoSlugSharing', { videoID, val })
    })
    .catch(error => {
      commit(mutation.notify, { level: 'danger', message: 'Problem enabling link sharing' })
      Raven.captureException(error)
    })
  },
  updateVideoCommenting ({ commit }, { videoID, commenting }) {
    return axios.patch(`/api/v1/videos/${videoID}/`, { commenting },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('updateVideoCommenting', { videoID, commenting })
    })
    .catch(error => {
      console.warn('error updating video commenting', error)
      throw error
    })
  },
  updateVideoSharing ({ commit }, { videoID, sharingType }) {
    return axios.patch(`/api/v1/videos/${videoID}/`, { shared_status: sharingType },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      const newShareStatus = response.data.shared_status
      commit('updateVideoSharing', { videoID, newShareStatus })
    })
    .catch(error => {
      console.warn('error updating video share_status', error)
      throw error
    })
  },
  deleteVideoByID ({ commit }, { videoID }) {
    return axios.delete(`/api/v1/videos/${videoID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('deleteVideoByID', videoID)
    })
    .catch(error => {
      console.warn('error deleting video by ID', error)
      throw error
    })
  },
  removeVideoByID ({ commit }, { videoID }) {
    return axios.delete(`/api/v1/videos/${videoID}/remove/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('deleteVideoByID', videoID)
    })
    .catch(error => {
      console.warn('error removing video by ID', error)
      throw error
    })
  },
  clipVideo ({ commit }, { videoID, startTime, endTime, replace }) {
    commit('setClippingVideo', true)
    return axios.post('/api/v1/clip/', { videoID, startTime, endTime, replace },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('setClippingVideo', false)
      commit(mutation.notify, { message: 'This video has been clipped!' })
    })
    .catch(error => {
      console.warn('error clipping video', error)
      commit('setClippingVideo', false)
      throw error
    })
  },
  checkVideoProcessed ({ commit }, { videoID }) {
    console.log('video ID', videoID)
    return axios.get(`/api/v1/videos/${videoID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data.processed)
    .then(processed => commit('updateVideoProcessed', { videoID, processed }))
    .catch(error => {
      console.warn('error with checking if video is processed', error)
      throw error
    })
  },

  updateFile ({ commit }, { fileID, title }) {
    return axios.patch(`/api/v1/resources/${fileID}/`, { title: title },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('updateFile', response.data)
    })
    .catch(error => {
      console.warn('error updating resource', error)
      throw error
    })
  },
  updateFileVideo ({ commit }, { videoID, title }) {
    return axios.patch(`/api/v1/videos/${videoID}/`, { title: title },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      const videoID = response.data.id
      const title = response.data.title
      commit('updateFileVideo', { videoID, title })
    })
    .catch(error => {
      console.warn('error updaing video in files', error)
      throw error
    })
  },
  createFile ({ commit }, { videoID, data }) {
    commit('setErrorAddingFile', false)
    commit('setAddingFile', true)
    return axios.post('/api/v1/resources/', data,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(resource => {
      commit('addFile', { videoID, resource })
      commit('setAddingFile', false)
      commit('toggleShowAddFileModal')
    })
    .catch(error => {
      commit('setErrorAddingFile', true)
      commit('setAddingFile', false)
      console.warn('error with creating file', error)
      throw error
    })
  },
  copyFile ({ commit, state }, { destVideoID, fileToCopy }) {
    const file = state.resources.find(x => x.id === fileToCopy)
    if (file === null) {
      console.log('error copying file to video. file not found')
      return
    }
    const newFile = { ...file, id: null, video: destVideoID, title: file.title + ' COPY' }
    const videoID = destVideoID
    commit('setErrorAddingFile', false)
    commit('setAddingFile', true)
    return axios.post('/api/v1/resources/', newFile,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(resource => {
      commit('toggleShowAddFileModal')
      commit('addFile', { videoID, resource })
      commit('setAddingFile', false)
      commit('setShowAddToVideoModal', false)
    })
    .catch(error => {
      commit('setErrorAddingFile', true)
      commit('setAddingFile', false)
      console.warn('error with creating file', error)
      commit('setShowAddToVideoModal', false)
      throw error
    })
  },
  [action.fetchNotifications] ({ commit }) {
    commit('setLoadingNotificationsData', true)
    return axios.get('/api/v1/notifications/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      const notifications = response.data
      commit('updateNotifications', notifications)

      commit('setLoadingNotificationsData', false)

      const notificationId = notifications.length > 0
        ? notifications[0].id
        : 0
      commit(mutation.setMostRecentNotification, notificationId)
      return response.data
    })
    .catch(error => {
      console.warn('error fetching notifications', error)
      commit('setLoadingNotificationsData', false)

      Raven.captureException(error)
    })
  },
  dismissNotification ({ commit }, id) {
    return axios.patch(`/api/v1/notifications/${id}/`, { dismissed: true },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('dismissNotification', id)
    })
    .catch(error => {
      console.warn('error dismissing notification', error)
      throw error
    })
  },
  [action.dismissAllNotifications] ({ commit }) {
    return axios.post('/api/v1/clear_notifications/', {},
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit(action.dismissAllNotifications)
    })
    .catch(error => {
      commit(mutation.notify, { message: 'failed to clear notifications', level: 'danger' })
      Raven.captureException(error)
    })
  },
  [action.pollNotifications] ({ commit }, lastFetch) {
    return axios.get('/api/v1/poll_notifications/', {
      headers: {
        'Authorization': 'Token ' + localStorage.getItem('token')
      },
      params: {
        last_fetch: lastFetch
      },
    })
    .then(response => {
      const notifications = response.data.new
      if (notifications.length > 0) {
        commit(mutation.addNewNotifications, notifications)
        commit(mutation.setMostRecentNotification, notifications[0].id)
      }
    })
  },

  addComment ({ commit }, data) {
    commit('setAddingComment', true)
    const videoID = data.video
    return axios.post('/api/v1/comments/', data,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('setAddingComment', false)
      commit('setDisplayAddComment', false)
      const newComment = response.data
      commit('addComment', { videoID, newComment })

      // Note: this is a bodge
      // mutations are synchronous but the DOM isn't updated synchronously
      // after the mutation
      setTimeout(() => {
        const el = document.querySelector(`#comment-${newComment.id}`)
        el.scrollIntoView(false)
        // scroll up some pixels to make room for outline
        // if we use border instead, I think scrollIntoView will take care of
        // this for us
        document.querySelector('.scroll-container').scrollTop += 5
        el.classList.add('new')
      }, 100)
    })
    .catch(error => {
      commit('setAddingComment', false)
      console.warn('error adding comment', error)
      throw error
    })
  },
  deleteComment ({ commit }, { videoID, commentID }) {
    return axios.delete(`/api/v1/comments/${commentID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('deleteComment', { videoID, commentID })
    })
    .catch(error => {
      console.warn('error deleting comment', error)
      throw error
    })
  },
  updateComment ({ commit }, { videoID, commentID, body, start_time, end_time }) {
    return axios.patch(`/api/v1/comments/${commentID}/`, { body, start_time, end_time },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(data => {
      const { body, end_time, start_time } = data
      commit('updateComment', {
        videoID,
        commentID,
        body,
        end_time,
        start_time,
      })
    })
    .catch(error => {
      console.warn('error updating comment', error)
      throw error
    })
  },

  addReply ({ commit }, { videoID, commentID, text }) {
    commit('setAddingReply', true)
    return axios.post('/api/v1/replies/', { body: text, comment: commentID },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(newComment => {
      commit('setAddingReply', false)
      commit('addReply', { videoID, commentID, newComment })
      commit('toggleReplytoComment', {
        videoID,
        commentID,
      })
    })
    .catch(error => {
      commit('setAddingReply', false)
      console.warn('error adding Reply', error)
      throw error
    })
  },
  deleteReply ({ commit }, { videoID, commentID, replyID }) {
    return axios.delete(`/api/v1/replies/${replyID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('deleteReply', { videoID, commentID, replyID })
    })
    .catch(error => {
      console.warn('error deleting Reply', error)
      throw error
    })
  },
  updateReply ({ commit }, { videoID, commentID, replyID, content }) {
    return axios.patch(`/api/v1/replies/${replyID}/`, { body: content },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data.body)
    .then(newReplyBody => {
      commit('updateReply', { videoID, commentID, replyID, newReplyBody })
    })
    .catch(error => {
      console.warn('error updating Reply', error)
      throw error
    })
  },

  fetchTeams ({ commit }) {
    commit('setLoadingTeamData', true)
    return axios.get('/api/v1/teams/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('setLoadingTeamData', false)
      commit('addTeams', response.data)
    })
    .catch(error => {
      commit('setLoadingTeamData', false)
      console.warn('error fetching teams', error)
      Raven.captureException(error)
      throw error
    })
  },
  fetchTeam ({ commit }, id) {
    commit('setLoadingTeamData', true)
    return axios.get(`/api/v1/teams/${id}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('setLoadingTeamData', false)
      commit('addTeam', response.data)
    })
    .catch(error => {
      commit('setLoadingTeamData', false)
      if (is404Error(error)) {
        return router.replace('/404')
      }
      console.warn(`error fetching team ${id}`, error)
      throw error
    })
  },
  addTeam ({ commit }, team) {
    commit('setLoadingTeamData', true)
    return axios.post('/api/v1/teams/', team,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('setLoadingTeamData', false)
      commit('addTeam', response.data)
      return response.data
    })
    .catch(error => {
      commit('setLoadingTeamData', false)
      if (is404Error(error)) {
        return router.replace('/404')
      }
      console.warn('error adding team', error)
      throw error
    })
  },
  deleteTeamByID ({ commit }, teamID) {
    return axios.delete(`/api/v1/teams/${teamID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('removeTeam', teamID)
    })
    .catch(error => {
      console.warn('error deleting team', error)
      throw error
    })
  },
  removeUserFromTeam ({ commit }, { teamID, userID }) {
    commit('setRemovingUserFromTeam', true)
    // Note: need to call axios in this way to be able to pass a body for a
    // delete method see https://github.com/mzabriskie/axios/issues/897 for
    // more info
    return axios({
      method: 'DELETE',
      url: `/api/v1/teams/${teamID}/members/`,
      data: { 'user': userID },
      headers: { 'Authorization': 'Token ' + localStorage.getItem('token') },
    })
    .then(() => {
      commit('setRemovingUserFromTeam', false)
      commit('removeUserFromTeam', { teamID, userID })
    })
    .catch(error => {
      commit('setRemovingUserFromTeam', false)
      console.warn('error removing user from team', error)
      throw error
    })
  },
  addUserToTeam ({ commit }, { teamID, user }) {
    commit('setAddingUserToTeam', true)
    return axios.post(`/api/v1/teams/${teamID}/members/`, { 'user': user.id },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('setAddingUserToTeam', false)
      commit('addUserToTeam', { teamID, user })
    })
    .catch(error => {
      commit('setAddingUserToTeam', false)

      console.warn('error adding user to team', error)
    })
  },

  fetchGroup ({ commit }) {
    commit('setLoadingGroup', true)
    return axios.get('/api/v1/groups/me/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('updateGroup', response.data)
      commit('setLoadingGroup', false)
    })
    .catch(error => {
      console.warn('error fetching groups', error)
      commit('setLoadingGroup', false)
    })
  },
  deleteResourceByID ({ commit }, resourceID) {
    return axios.delete(`/api/v1/resources/${resourceID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('deleteFile', resourceID)
    })
    .catch(error => {
      console.warn('error deleting resource', error)
    })
  },
  deleteResource ({ commit }, { videoID, resourceID }) {
    return axios.delete(`/api/v1/resources/${resourceID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('deleteResource', { videoID, resourceID })
    })
    .catch(error => {
      console.warn('error deleting resource', error)
    })
  },
  updateResource ({ commit }, { videoID, resourceID, newTitle }) {
    return axios.patch(`/api/v1/resources/${resourceID}/`, { title: newTitle },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data.title)
    .then(newTitle => {
      commit('updateResource', { videoID, resourceID, newTitle })
    })
    .catch(error => {
      console.warn('error updating resource', error)
    })
  },

  updateGroup ({ commit, dispatch }, group) {
    return axios.patch(`/api/v1/teams/${group.id}/`, group,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(res => {
      commit('updateTeam', { ...group, ...res.data })
      return res.data
    })
    .catch(error => dispatch(action.generalErrorHandler, { error, message: "Uh Oh. Group information couldn't be updated." }))
  },

  fetchActiveExercise ({ commit }) {
    commit('setLoadingExercises', true)
    return axios.get('/api/v1/exercises/active/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(exercise => {
      commit('updateActiveExercise', exercise)
      commit('setLoadingExercises', false)
    })
    .catch(error => {
      console.warn('error fetching active exercise', error)
      commit('setLoadingExercises', false)
      if (is404Error(error)) {
        router.replace('/404')
      }
      throw error
    })
  },

  fetchExercises ({ commit }) {
    commit('setLoadingExercises', true)
    return axios.get('/api/v1/exercises/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(exercises => {
      commit('updateExercises', exercises)
      commit('setLoadingExercises', false)
    })
    .catch(error => {
      console.warn('error fetching exercises', error)
      commit('setLoadingExercises', false)
      if (is404Error(error)) {
        router.replace('/404')
      }
      throw error
    })
  },
  fetchExercise ({ commit }, exerciseID) {
    return axios.get(`/api/v1/exercises/${exerciseID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(exercise => {
      commit('updateExercise', exercise)
      return exercise
    })
    .catch(error => {
      console.warn('error fetching exercise', error)
      throw error
    })
  },
  createExercise ({ commit }, {
    title,
    prompt,
    notes,
    video,
    allowResponses,
    responseDeadline,
    allowComments,
    commentsVisible,
    commentDeadline,
    questions,
    }) {
    commit('setSavingExercise', true)
    questions = questions.map ? questions.map(q => {
      return {
        text: q.text,
        rubric_element: q.rubricId,
        text_prompt: q.enableTextResponse,
        video_prompt: q.enableVideoResponse,
      }
    }) : questions
    return axios.post('/api/v1/exercises/',
      {
        title,
        prompt,
        notes,
        video,
        allow_responses: allowResponses,
        response_deadline: responseDeadline,
        allow_comments: allowComments,
        comments_visible: commentsVisible,
        comment_deadline: commentDeadline,
        questions,
      },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(exercise => {
      commit('addExercise', exercise)
      commit('setSavingExercise', false)
      commit(mutation.notify, { message: 'created exercise', level: 'success' })
    })
    .then(() => router.push('/exercises'))
    .catch(error => {
      console.warn('error saving exercise', error)
      commit('setSavingExercise', false)
      commit(mutation.notify, { message: 'failed to create exercise', level: 'danger' })
      throw error
    })
  },
  saveResponse ({ commit }, { exerciseID, questionID, data }) {
    const { question, video, rubric_score, text } = data
    commit('setSavingResponse', true)
    return axios.post('/api/v1/answers/', { question, video, rubric_score, text },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(answer => {
      commit('saveResponse', { exerciseID, questionID, answer })
      commit('setSavingResponse', false)
    })
    .catch(error => {
      console.warn('error saving response', error)
      commit('setSavingResponse', false)
      throw error
    })
  },
  updateResponse ({ commit }, { exerciseID, questionID, answerID, data }) {
    commit('setUpdatingResponse', true)
    return axios.patch(`/api/v1/answers/${answerID}/`, data,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(data => {
      commit('updateResponse', { exerciseID, questionID, answerID, data })
      commit('setUpdatingResponse', false)
    })
    .catch(error => {
      console.warn('error updating response', error)
      commit('setUpdatingResponse', false)
      throw error
    })
  },
  fetchRubricElements ({ commit }) {
    commit('setLoadingRubricElements', true)
    return axios.get('/api/v1/rubric-elements/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(data => {
      commit('updateRubricElements', data)
      commit('setLoadingRubricElements', false)
    })
    .catch(error => {
      console.warn('error fetching rubric elements', error)
      commit('setLoadingRubricElements', false)
      throw error
    })
  },
  addRubricOptiontoComment ({ commit }, { videoID, commentID, element, descriptor }) {
    // TODO: mutations
    commit('setAddingRubricElementtoComment', true)
    if (descriptor === 0) {
      descriptor = null
    }
    return axios.post(`/api/v1/comments/${commentID}/elements/`, { element, selected: descriptor },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => response.data)
    .then(data => {
      commit('addRubricElementtoComment', { videoID, commentID, rubricElement: data })
      commit('setAddingRubricElementtoComment', false)
    })
    .catch(error => {
      console.warn('error adding rubric option to comment', error)
      commit('setAddingRubricElementtoComment', false)
      throw error
    })
  },
  deleteRubricOptionFromComment ({ commit }, { videoID, commentID, id }) {
    // TODO: add a loading mutation
    return axios.delete(`/api/v1/comments/${commentID}/elements/${id}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('removeRubricElementfromComment', { videoID, commentID, rubricElementID: id })
    })
    .catch(error => {
      console.warn('error removing rubric option from comment', error)
      throw error
    })
  },

  fetchLibraries ({ commit }) {
    commit('setLoadingLibraryData', true)
    return axios.get('/api/v1/libraries/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        commit('setLibraries', response.data)
        commit('setLoadingLibraryData', false)
      })
      .catch(error => {
        console.warn('error fetching libraries', error)
        commit('setLoadingLibraryData', false)
        commit(mutation.notify, { message: 'Oops! Something did not work' })
        throw error
      })
  },
  fetchLibrary ({ commit }, id) {
    commit('setLoadingLibraryData', true)
    return axios.get(`/api/v1/libraries/${id}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        commit('setActiveLibrary', response.data)
        commit('setActiveLibraryID', response.data.id)
        commit('setLoadingLibraryData', false)
      })
      .catch(error => {
        console.warn('error fetching library', error)
        commit('setLoadingLibraryData', false)
        commit(mutation.notify, { message: 'Oops! Something did not work' })
        throw error
      })
  },
  updateLibrary ({ commit }, { libraryID, title, description }) {
    commit('setLoadingLibraryData', true)
    return axios.patch(`/api/v1/libraries/${libraryID}/`, { title, description },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(() => {
        commit('setLoadingLibraryData', false)
      })
      .catch(error => {
        console.warn('error updating library', error)
        commit('setLoadingLibraryData', false)
        commit(mutation.notify, { message: 'Oops! Something did not work' })
        throw error
      })
  },
  updateVideoLibrary ({ commit }, { videoID, libraryID }) {
    return axios.patch(`/api/v1/videos/${videoID}/`, { library: libraryID },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('removeVideoFromLibrary', videoID)
      console.log('Removed video from library')
    })
    .catch(error => {
      console.warn('error removing video from library', error)
      throw error
    })
  },

  shareVideoWithUser ({ commit }, { videoID, userID }) {
    return axios.post('/api/v1/videoshares/', { user: userID, video: videoID },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('updateVideoShare', { videoID, newShare: response.data })
      commit(mutation.notify, { message: 'Your video has been shared!' })
    })
    .catch(error => {
      console.warn('error sharing video', error)
      throw error
    })
  },
  shareVideoWithTeam ({ commit }, { videoID, teamID }) {
    return axios.post('/api/v1/teamshares/', { video: videoID, team: teamID },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        commit('updateTeamShare', { videoID, newTeamShare: response.data })
        commit(mutation.notify, { message: 'Your video has been shared with the team!' })
      })
      .catch(error => {
        console.warn('error sharing video', error)
        throw error
      })
  },
  unshareVideoWithUser ({ commit }, { videoID, videoShareID }) {
    return axios.delete(`/api/v1/videoshares/${videoShareID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(() => {
        commit('removeVideoShare', { videoID, videoShareID })
        commit(mutation.notify, { message: 'Your video is no longer shared with that person, even if they are part of a team you have shared with.' })
      })
      .catch(error => {
        console.warn('error sharing video', error)
        throw error
      })
  },
  unshareVideoWithTeam ({ commit }, { videoID, teamShareID }) {
    commit(mutation.setUnsharingTeamShare, { videoID, teamShareID, val: true })
    return axios.delete(`/api/v1/teamshares/${teamShareID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(() => {
        commit('removeTeamShare', { videoID, teamShareID })
        commit(mutation.setUnsharingTeamShare, { videoID, teamShareID, val: false })
        commit(mutation.notify, { message: 'Your video is no longer shared with people on that team.' })
      })
      .catch(error => {
        console.warn('error sharing video', error)
        commit(mutation.setUnsharingTeamShare, { videoID, teamShareID, val: false })
        Raven.captureException(error)
        throw error
      })
  },

  signup ({ commit }, { email, password1, password2 }) {
    return axios.post('/api/v1/rest-auth/registration/', { email, password1, password2 })
      .then(response => response.data.key)
      .then(token => {
        commit('authenticateUser', { token })
        router.push('/')
      })
  },

  [action.socialConnect] ({ commit, dispatch }, { provider, code }) {
    return axios.post(`/api/v1/rest-auth/${provider}/connect/`, { code },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(() => {
        router.replace('/settings')
      })
      .catch(error => {
        if (error.response && isBadRequest(error)) {
          const data = error.response.data
          commit(mutation.setErrorSocialLogin, {
            email: data['email'],
            nonFieldErrors: data['non_field_errors']
          })
        } else {
          dispatch(action.generalErrorHandler, { error, message: 'Uh Oh! There was a problem connecting your social account.' })
        }
        router.replace('/settings')
      })
  },

  [action.socialDisconnect] ({ commit, dispatch }, id) {
    return axios.post(`/api/v1/rest-auth/socialaccounts/${id}/disconnect/`, {},
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit(mutation.notify, { message: 'Disconnected social account.', level: 'success' })
      commit(mutation.removeProviderByID, id)
    })
    .catch(error => {
      if (error.response && isBadRequest(error)) {
        const data = error.response.data
        commit(mutation.setErrorSocialLogin, {
          email: data['email'],
          nonFieldErrors: data['non_field_errors']
        })
      } else {
        dispatch(action.generalErrorHandler, { error, message: 'Uh Oh! There was disconnecting your social account.' })
      }
    })
  },

  [action.socialAuth] (context, { provider, code, redirectErrorPath }) {
    return axios.post(`/api/v1/rest-auth/${provider}/`, { code })
      .then(response => response.data.key)
      .then(token => {
        context.commit(mutation.setErrorSocialLogin, {})
        context.commit('authenticateUser', { token })
        router.replace('/')
      })
      .catch(error => {
        if (error.response && isBadRequest(error)) {
          const data = error.response.data
          context.commit(mutation.setErrorSocialLogin, {
            email: data['email'],
            nonFieldErrors: data['non_field_errors']
          })
        } else {
          context.dispatch(action.generalErrorHandler, { error, message: 'Uh Oh! There was a problem authenticating with your social account.' })
        }
        router.replace(redirectErrorPath)
      })
  },

  [action.socialLogin] ({ dispatch }, { provider, code }) {
    return dispatch(action.socialAuth, { provider, code, redirectErrorPath: '/login' })
  },

  [action.socialSignup] ({ dispatch }, { provider, code }) {
    return dispatch(action.socialAuth, { provider, code, redirectErrorPath: '/signup' })
  },

  [action.invalidOAuthState] ({ dispatch, commit }, { route = 'signup' }) {
    router.replace(`/${route}`)
    commit(mutation.setErrorSocialLogin, {
      nonFieldErrors: ['Your social account provider served an invalid secret.']
    })
    return dispatch(action.generalErrorHandler, { error: "OAuth state tokens don't match", message: 'Uh Oh! There was a problem authenticating with your social account.' })
  },

  [action.fetchSocialAccountConnections] (context) {
    return axios.get('/api/v1/rest-auth/socialaccounts/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(res => {
        context.commit(mutation.updateSocialConnections, res.data)
      })
      .catch(error => {
        context.dispatch(action.generalErrorHandler, { error, message: false })
      })
  },

  requestInfo (_, { email, group }) {
    return axios.post('/api/v1/signups/', { email, group, info: true })
    .catch(error => {
      console.warn('error signing up', error)
      throw error
    })
  },
  requestDemo (_, { email, group, name, phone }) {
    return axios.post('/api/v1/signups/', { email, group, name, phone, demo: true })
    .catch(error => {
      console.warn('error signing up', error)
      throw error
    })
  },

  addColleague ({ commit }, { colleagueID }) {
    return axios.post(`/api/v1/users/${colleagueID}/colleague/`, null,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('addColleague', response.data)
    })
    .catch(error => {
      console.warn('error adding colleague', error)
      throw error
    })
  },
  removeColleague ({ commit }, { colleague }) {
    return axios.delete(`/api/v1/users/${colleague.id}/colleague/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('removeColleague', colleague.id)
      commit(mutation.notify, { message: `You are no longer connected to ${colleague.name || colleague.email}`, level: 'warning' })
    })
    .catch(error => {
      console.warn('error removing colleague', error)
      throw error
    })
  },
  sendColleagueRequest ({ commit }, { colleague }) {
    return axios.post(`/api/v1/users/${colleague.id}/request/`, null,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit(mutation.notify, { message: `Sent a request to ${colleague.name || colleague.email}`, level: 'success' })
    })
    .catch(error => {
      console.warn('error adding colleague', error)
      throw error
    })
  },
  denyColleagueRequest ({ commit }, { colleagueID }) {
    return axios.delete(`/api/v1/users/${colleagueID}/request/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit('denyColleagueRequest', colleagueID)
    })
    .catch(error => {
      console.warn('error removing colleague', error)
      throw error
    })
  },

  [action.joinTeam] ({ commit }, teamId) {
    return axios.post(`/api/v1/teams/${teamId}/join/`, null,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        commit(mutation.notify, { message: response.data.message, level: 'success' })
        return response
      })
      .catch(error => {
        console.warn('error joining', error)
        throw error
      })
  },

  [action.leaveTeam] ({ commit, dispatch }, teamId) {
    return axios.delete(`/api/v1/teams/${teamId}/join/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        console.log(response)
        commit(mutation.notify, { message: 'Successfully left team.', level: 'success' })
        commit(mutation.removeTeam, teamId)
        return response
      })
      .catch(error => {
        dispatch(action.generalErrorHandler, {
          error,
          message: 'Uh Oh! There was a problem leaving the team.'
        })
        throw error
      })
  },

  fetchPendingMembers ({ commit }, { teamID }) {
    commit('setLoadingMembers', true)
    return axios.get(`/api/v1/teams/${teamID}/pending/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        commit('updatePendingMembers', response.data)
        commit('setLoadingMembers', false)
        return response
      })
      .catch(error => {
        console.warn('error fetching pending members', error)
        commit('setLoadingMembers', false)
        throw error
      })
  },
  approveUser ({ commit }, { teamID, user }) {
    return axios.post(`/api/v1/teams/${teamID}/approve/`, { user: user.id },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        commit('updatePendingMembers', response.data)
        commit('addUserToTeam', { teamID, user })
        return response
      })
      .catch(error => {
        console.warn('error approving user', error)
        throw error
      })
  },
  denyUser ({ commit }, { teamID, user }) {
    return axios.post(`/api/v1/teams/${teamID}/deny/`, { user: user.id },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => {
        commit('updatePendingMembers', response.data)
        return response
      })
      .catch(error => {
        console.warn('error denying user', error)
        throw error
      })
  },

  createInvite ({ commit }, email) {
    return axios.post('/api/v1/invites/', email,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('addInvite', response.data)
      commit(mutation.notify, { message: 'Successfully sent invite.', level: 'success' })
    })
    .catch(error => {
      commit(mutation.notify, { message: error.response.data['email'][0], level: 'danger' })
      throw error
    })
  },
  resendInvite ({ commit }, { inviteID, invite }) {
    return axios.post(`/api/v1/invites/${inviteID}/resend/`, { invite },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      commit(mutation.notify, { message: 'email sent', level: 'success' })
    })
    .catch(error => {
      commit(mutation.notify, { message: 'error sending email', level: 'danger' })
      Raven.captureException(error)
    })
  },
  addUser ({ commit }, { name, email, isCoach, isTeacher, sendEmail }) {
    return axios.post('/api/v1/invites/provision/', { name, email, is_coach: isCoach, is_teacher: isTeacher, send_email: sendEmail },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('addUserToGroup', response.data)
      commit(mutation.notify, { message: 'Account created for ' + email })
    })
    .catch(error => {
      console.warn('error creating acount', error)
      throw error
    })
  },

  [action.fetchTags] (context) {
    return axios.get('/api/v1/tags/',
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      context.commit('updateTags', response.data)
    })
    .catch(error => {
      console.warn('error fetching tags', error)
      Raven.captureException(error)
    })
  },
  [action.createTag] (context, { title, createRubric }) {
    return axios.post('/api/v1/tags/', { title, create_rubric: createRubric },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      context.commit('addTag', response.data)
    })
    .catch(error => {
      console.warn('error creating tag', error)
      Raven.captureException(error)
    })
  },
  [action.deleteTag] (context, tagID) {
    return axios.delete(`/api/v1/tags/${tagID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('removeTag', tagID)
    })
    .catch(error => {
      console.warn('error deleting tag', error)
      Raven.captureException(error)
    })
  },
  [action.archiveTag] (context, tagID) {
    return axios.delete(`/api/v1/tags/${tagID}/archive/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('archiveTag', tagID)
    })
    .catch(error => {
      console.warn('error archiving tag', error)
      Raven.captureException(error)
    })
  },
  [action.restoreTag] (context, tagID) {
    return axios.post(`/api/v1/tags/${tagID}/archive/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('removeTag', tagID)
    })
    .catch(error => {
      console.warn('error restoring tag', error)
      Raven.captureException(error)
    })
  },
  [action.updateTagShare] (context, { tagID, groupTag }) {
    return axios.patch(`/api/v1/tags/${tagID}/`, { group_tag: groupTag },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('updateTagShare', { tagID, groupTag })
    })
    .catch(error => {
      console.warn('error updating tag share', error)
      Raven.captureException(error)
    })
  },
  [action.updateTag] (context, { tagID, title, color }) {
    return axios.patch(`/api/v1/tags/${tagID}/`, { title, color },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('updateTag', { tagID, title, color })
    })
    .catch(error => {
      console.warn('error updating tag title', error)
      Raven.captureException(error)
    })
  },
  [action.updateRubricItem] (context, { rubricID, header1, header2, header3, header4, header5, desc1, desc2, desc3, desc4, desc5 }) {
    return axios.patch(`/api/v1/rubrics/${rubricID}/`, { header1, header2, header3, header4, header5, desc1, desc2, desc3, desc4, desc5 },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('updateRubricItem', { rubricID, header1, header2, header3, header4, header5, desc1, desc2, desc3, desc4, desc5 })
    })
    .catch(error => {
      console.warn('error updating rubric item', error)
      Raven.captureException(error)
    })
  },
  [action.addTagToComment] ({ commit }, { videoID, tagID, commentID }) {
    return axios.post(`/api/v1/comments/${commentID}/tags/`, { tag: tagID },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      commit('addTagToComment', {
        videoID: videoID,
        commentID: commentID,
        tag: response.data,
      })
    })
    .catch(error => {
      console.warn('error adding tag to comment', error)
      Raven.captureException(error)
      throw error
    })
  },
  [action.removeTagFromComment] (context, { videoID, tagID, commentID }) {
    return axios.delete(`/api/v1/comments/${commentID}/tags/${tagID}/`,
      { data: { tag: tagID }, headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('removeTagFromComment', { videoID, commentID, tagID })
    })
    .catch(error => {
      console.warn('error removing tag from comment', error)
      Raven.captureException(error)
    })
  },

  [action.addScoreToComment] (context, { value, tagID, commentID, videoID }) {
    return axios.post('/api/v1/scores/', { tag: tagID, comment: commentID, value },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      context.commit('addScoreToComment', { score: response.data, videoID, commentID })
    })
    .catch(error => {
      console.warn('error adding score to comment', error)
      Raven.captureException(error)
    })
  },
  [action.removeScoreFromComment] (context, { scoreID, videoID, commentID }) {
    return axios.delete(`/api/v1/scores/${scoreID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('removeScoreFromComment', { scoreID, commentID, videoID })
    })
    .catch(error => {
      console.warn('error removing score from comment', error)
      Raven.captureException(error)
    })
  },
  [action.leaveFolder] (context, folderID) {
    return axios.delete(`/api/v1/folders/${folderID}/leave/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('removeFolder', folderID)
      context.commit(mutation.notify, { message: 'Successfully left shared folder.', level: 'danger' })
    })
    .catch(error => {
      console.warn('error removing folder', error)
      Raven.captureException(error)
    })
  },
  [action.shareFolderWithUser] (context, { folderID, userID }) {
    return axios.post('/api/v1/foldershares/', { folder: folderID, user: userID },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      context.commit('addFolderShare', { folderShare: response.data, folderID })
      context.commit(mutation.notify, { message: 'Successfully shared folder.', level: 'success' })
    })
    .catch(error => {
      console.warn('error sharing folder', error)
      Raven.captureException(error)
    })
  },
  [action.shareFolderWithTeam] (context, { folderID, teamID }) {
    return axios.post('/api/v1/teamfoldershares/', { folder: folderID, team: teamID },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(response => {
      context.commit('addTeamFolderShare', { teamFolderShare: response.data, folderID })
      context.commit(mutation.notify, { message: 'Successfully shared folder.', level: 'success' })
    })
    .catch(error => {
      console.warn('error sharing folder', error)
      Raven.captureException(error)
    })
  },
  [action.unshareFolderWithUser] (context, { folderID, folderShareID }) {
    return axios.delete(`/api/v1/foldershares/${folderShareID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('removeFolderShare', { folderID, folderShareID })
      context.commit(mutation.notify, { message: 'Successfully unshared folder.', level: 'success' })
    })
    .catch(error => {
      console.warn('error unsharing folder', error)
      Raven.captureException(error)
    })
  },
  [action.unshareFolderWithTeam] (context, { folderID, teamFolderShareID }) {
    return axios.delete(`/api/v1/teamfoldershares/${teamFolderShareID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('removeTeamFolderShare', { folderID, teamFolderShareID })
      context.commit(mutation.notify, { message: 'Successfully unshared folder.', level: 'success' })
    })
    .catch(error => {
      console.warn('error unsharing folder', error)
      Raven.captureException(error)
    })
  },

  [action.deleteFolder] (context, folderID) {
    return axios.delete(`/api/v1/folders/${folderID}/`,
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .then(() => {
      context.commit('removeFolder', folderID)
      context.commit(mutation.notify, { message: 'Successfully deleted folder.', level: 'danger' })
    })
    .catch(error => {
      console.warn('error removing folder', error)
      Raven.captureException(error)
    })
  },
  [action.createEventCF] (_, { message }) {
    return axios.post('/api/v1/events/', { message },
      { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
    .catch(error => {
      console.warn('error creating eventcf', error)
      Raven.captureException(error)
    })
  },
}

export default actions



// WEBPACK FOOTER //
// ./src/actions.js